import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';

import { SettingsPageFrame } from '@eluve/blocks';
import {
  Button,
  FormFooter,
  FormHeader,
  FormNewDescription,
  FormSection,
  FormTitle,
  Textarea,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { useBeforeUnload } from '@eluve/utility-hooks';

/** Matches the extraction prompt's clamp (MAX_CUSTOM_INSTRUCTIONS_CHARS). */
const MAX_INSTRUCTIONS_CHARS = 2000;

const taskInstructionsSettingsQuery = graphql(`
  query taskInstructionsSettings($tenantId: uuid!, $userId: uuid!) {
    tenantUserSettingsByPk(tenantId: $tenantId, userId: $userId) {
      __typename
      tenantId
      userId
      customTaskExtractionInstructions
    }
  }
`);

const updateTaskInstructionsSettingsMutation = graphql(`
  mutation updateTaskInstructionsSettings(
    $tenantId: uuid!
    $userId: uuid!
    $instructions: String
  ) {
    updateTenantUserSettingsByPk(
      pkColumns: { tenantId: $tenantId, userId: $userId }
      _set: { customTaskExtractionInstructions: $instructions }
    ) {
      __typename
      tenantId
      userId
      customTaskExtractionInstructions
    }
  }
`);

/**
 * Settings > Preferences > Tasks: the user-level task-extraction
 * instructions (ENG-5451 alpha). Same value the appointment-panel gear
 * dialog edits — sticky per user+tenant, applied to every extraction
 * (automatic and regenerate). Saving here never triggers a regenerate.
 */
export const TaskSettingsPage: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const toast = useToast();
  const { t } = useTranslation();

  const { data } = useQuery(taskInstructionsSettingsQuery, {
    variables: { tenantId, userId },
  });
  const saved =
    data?.tenantUserSettingsByPk?.customTaskExtractionInstructions ?? null;

  const [instructions, setInstructions] = useState<string>('');
  // Seed once the saved value arrives; later cache updates re-seed only
  // while the field is untouched (dirty edits must not be clobbered).
  useEffect(() => {
    setInstructions((prev) => (prev === '' ? (saved ?? '') : prev));
  }, [saved]);

  // Trim-normalized: saves persist the trimmed value, so whitespace-only
  // differences are not unsaved work (dirty here would pin Save enabled and
  // warn on unload for content that is already persisted).
  const isDirty = (instructions.trim() || null) !== saved;

  useBeforeUnload(isDirty);

  const [update] = useMutation(updateTaskInstructionsSettingsMutation, {
    onError: () => toast.error(t('settings.tasks.saveInstructionsFailed')),
  });

  const saveInstructions = async () => {
    // Whitespace-only saves as cleared (NULL = default extraction).
    const trimmed = instructions.trim();
    const res = await update({
      variables: { tenantId, userId, instructions: trimmed || null },
    });
    // Sync the field to what was persisted so the form reads clean.
    if (res.data?.updateTenantUserSettingsByPk) setInstructions(trimmed);
  };

  return (
    <SettingsPageFrame title={t('settings.tasks.title')}>
      <FormSection className="gap-4">
        <FormHeader>
          <FormTitle>{t('settings.tasks.customInstructions')}</FormTitle>
          <FormNewDescription>
            {t('settings.tasks.customInstructionsDescription')}
          </FormNewDescription>
        </FormHeader>
        <Textarea
          placeholder={t('settings.tasks.instructionsPlaceholder')}
          rows={4}
          maxLength={MAX_INSTRUCTIONS_CHARS}
          className="min-h-25 bg-bg-primary"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          data-testid="task-settings-instructions-input"
        />
        <FormFooter className="gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={!isDirty}
            onClick={() => setInstructions(saved ?? '')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!isDirty}
            onClick={saveInstructions}
            data-testid="task-settings-instructions-save"
          >
            {t('settings.common.save')}
          </Button>
        </FormFooter>
      </FormSection>
    </SettingsPageFrame>
  );
};
